import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useExternalKeyTermsListQuery } from '@client/graphql/__generated__/document-operations';
import {
  NotificationType,
  TransactionNotificationLogFragment,
} from '@client/graphql/__generated__/types';
import Mention from '@tiptap/extension-mention';
import { EditorContent, Extension, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Fuse from 'fuse.js';
import handlebars from 'handlebars';
import * as htmlToText from 'html-to-text';
import { useEffect, useMemo } from 'react';
import { enumToLabel } from '~/services/document/utils/enums';
import { EmailPreview } from '../NotificationsList/EmailPreview';
import { SmsPreview } from '../NotificationsList/SmsPreview';
import { MentionItem } from './MentionSuggestion';
import { createSuggestion } from './suggestion';

import type { NormalizedOfferFieldValues } from '@common/entities';
import { faker } from '@faker-js/faker';
import './MessageEditor.css';

interface MessageEditorProps {
  value: string;
  onChange: (value: string) => void;
  notificationType: NotificationType;
  title: string;
}

const KEY_TERM_VALUES: NormalizedOfferFieldValues = {
  selling_agent_name: faker.person.fullName(),
  selling_agent_email: faker.internet.email(),
  selling_agent_broker_name: faker.company.name(),
  buyer_name: faker.person.fullName(),
  co_buyer_name: faker.person.fullName(),
  seller_name: faker.person.fullName(),
  co_seller_name: faker.person.fullName(),
  close_date: faker.date.future().toISOString(),
  financing_type: 'FHA',
  listing_agent_broker_name: faker.person.fullName(),
  listing_agent_name: faker.person.fullName(),
  listing_agent_email: faker.internet.email(),
  property_city: 'Anytown',
  property_state: 'NY',
  property_zip_code: '12345',
  property_street_address: '123 Main St',
  property_full_address: '123 Main St #99, Anytown, NY 12345',
  property_unit: '#99',
  id: '1',
  updated_at: Date.now(),
};

const domParser = new DOMParser();

export const MessageEditor = ({
  value,
  onChange,
  notificationType,
  title,
}: MessageEditorProps) => {
  const { data: externalKeyTermsListData } = useExternalKeyTermsListQuery();

  const mentionItems = useMemo(() => {
    return externalKeyTermsListData?.externalKeyTermsList?.map((field) => ({
      id: field.columnName,
      label: enumToLabel(field.columnName),
    }));
  }, [externalKeyTermsListData]);

  const searchItems = useMemo(() => {
    return new Fuse<MentionItem>(mentionItems || [], {
      keys: ['label', 'id'],
      threshold: 0.5,
      distance: 100,
      isCaseSensitive: true,
    });
  }, [mentionItems]);

  const extensions = useMemo(() => {
    const all: Extension[] = [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
    ];

    if (mentionItems) {
      all.push(
        Mention.configure({
          HTMLAttributes: { class: 'mention' },
          suggestion: createSuggestion(mentionItems, searchItems),
        }) as Extension
      );
    }

    return all;
  }, [mentionItems, searchItems]);

  const editor = useEditor(
    {
      extensions,
      content: value,
      onUpdate: ({ editor }) => onChange(editor.getHTML()),
    },
    [extensions]
  );

  // Sync external value changes to editor
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  const messageContentTemplate = useMemo(() => {
    const parsed = domParser.parseFromString(value, 'text/html');
    parsed
      .querySelectorAll('[data-type="mention"]')
      .forEach((mention: Element) => {
        const htmlMention = mention as HTMLElement;
        mention.replaceWith(`{{${htmlMention.dataset.id}}}`);
      });

    if (notificationType === NotificationType.EMAIL) {
      return handlebars.compile(parsed.body?.innerHTML ?? '')(KEY_TERM_VALUES);
    }

    if (notificationType === NotificationType.TEXT) {
      const removedHTML = htmlToText.htmlToText(parsed.body?.innerHTML ?? '');

      return handlebars.compile(removedHTML ?? '')(KEY_TERM_VALUES);
    }

    return '';
  }, [value, notificationType]);

  const mockNotification: TransactionNotificationLogFragment = useMemo(
    () => ({
      __typename: 'TransactionNotificationLogObject',
      id: 'preview',
      title: title || 'Preview',
      messageContent: messageContentTemplate,
      createdAt: new Date().toISOString(),
      recipients: [],
    }),
    [title, messageContentTemplate]
  );

  return (
    <VStack alignItems="stretch" spacing={4}>
      <Box
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="md"
      >
        {/* Toolbar */}
        <Flex
          alignItems="center"
          bg="whiteAlpha.50"
          borderBottom="1px solid"
          borderColor="whiteAlpha.100"
          gap={4}
          px={5}
          py={3}
        >
          <Text
            color="whiteAlpha.700"
            fontSize="xs"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Message
          </Text>
          <HStack spacing={1}>
            <IconButton
              aria-label="Bold"
              bg={editor?.isActive('bold') ? 'whiteAlpha.200' : 'transparent'}
              icon={<Text fontWeight="bold">B</Text>}
              size="sm"
              variant="ghost"
              onClick={() => editor?.chain().focus().toggleBold().run()}
            />
            <IconButton
              aria-label="Italic"
              bg={editor?.isActive('italic') ? 'whiteAlpha.200' : 'transparent'}
              icon={<Text fontStyle="italic">I</Text>}
              size="sm"
              variant="ghost"
              onClick={() => editor?.chain().focus().toggleItalic().run()}
            />
          </HStack>
          <Text color="whiteAlpha.700" fontSize="sm" fontWeight="medium">
            Type @ to insert a variable
          </Text>
        </Flex>
        <EditorContent className="tiptap-editor" editor={editor} />
      </Box>

      <Divider />

      {/* Preview Section */}
      {value && (
        <>
          <Heading
            fontSize="xl"
            fontWeight="bold"
            lineHeight="1"
            size="xs"
            textTransform="uppercase"
          >
            Preview
          </Heading>
          <Box
            border="1px solid"
            borderColor="whiteAlpha.100"
            borderRadius="md"
            p={4}
          >
            {notificationType === NotificationType.EMAIL ? (
              <EmailPreview notification={mockNotification} />
            ) : (
              <SmsPreview notification={mockNotification} />
            )}
          </Box>
        </>
      )}
    </VStack>
  );
};
